import { useEffect, useMemo, useState } from "react";
import { useLocation } from "wouter";
import { trpc } from "../../lib/trpc.js";
import { useT } from "../../i18n/index.js";

type CompanyOption = {
  id: number;
  name: string;
};

type ServiceType = "dsp" | "montage_demontage";
type Base = "orcamento" | "faturado";

interface HeaderForm {
  billingCompanyId: number | null;
  receivingCompanyId: number | null;
  customerId: number | null;
  vehicleId: number | null;
  plate: string;
  vin: string;
  invoiceNumberExternal: string;
  invoiceDate: string;
  customerRetentionPct: string;
  notes: string;
}

interface LineForm {
  enabled: boolean;
  serviceType: ServiceType;
  responsibleCompanyId: number | null;
  technicianId: number | null;
  orcamento: string;
  faturado: string;
  technicianAmountKnown: boolean;
  technicianCommissionPct: string;
  technicianCommissionBase: Base | "";
  technicianCommissionAmount: string;
  notes: string;
}

export function DossierWizard({
  companyId,
  accessibleCompanies,
  onClose,
  onCreated,
}: {
  companyId: number;
  accessibleCompanies: ReadonlyArray<CompanyOption>;
  onClose: () => void;
  onCreated: (dossierId: number) => void;
}) {
  const [, setLocation] = useLocation();
  const tr = useT();
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [error, setError] = useState<string | null>(null);
  // F-trava-duplicata — o carro já tem dossier em aberto: mostra qual e deixa
  // o operador confirmar que é um serviço NOVO.
  const [duplicado, setDuplicado] = useState<string | null>(null);

  const today = new Date().toISOString().slice(0, 10);
  const [header, setHeader] = useState<HeaderForm>({
    billingCompanyId: companyId,
    receivingCompanyId: null,
    customerId: null,
    vehicleId: null,
    plate: "",
    vin: "",
    invoiceNumberExternal: "",
    invoiceDate: today,
    customerRetentionPct: "",
    notes: "",
  });

  const [line, setLine] = useState<LineForm>({
    enabled: true,
    serviceType: "dsp",
    responsibleCompanyId: companyId,
    technicianId: null,
    orcamento: "",
    faturado: "",
    technicianAmountKnown: true,
    technicianCommissionPct: "",
    technicianCommissionBase: "",
    technicianCommissionAmount: "",
    notes: "",
  });

  const customersQuery = trpc.customers.list.useQuery({
    companyId,
    active: true,
    limit: 200,
    offset: 0,
  });
  const vehiclesQuery = trpc.vehicles.listByCustomer.useQuery(
    { companyId, customerId: header.customerId ?? 0 },
    { enabled: header.customerId !== null },
  );
  const techniciansQuery = trpc.technicians.list.useQuery({
    companyId,
    active: true,
    limit: 200,
    offset: 0,
  });

  const customers = customersQuery.data?.rows ?? [];
  const vehicles = vehiclesQuery.data ?? [];
  const technicians = techniciansQuery.data?.rows ?? [];

  const createDossier = trpc.dossiers.create.useMutation();
  const createLine = trpc.serviceLines.create.useMutation();

  const submitting = createDossier.isPending || createLine.isPending;

  const selectedCustomer = useMemo(
    () => customers.find((c) => c.id === header.customerId) ?? null,
    [customers, header.customerId],
  );
  const selectedTechnician = useMemo(
    () => technicians.find((t) => t.id === line.technicianId) ?? null,
    [technicians, line.technicianId],
  );

  // F-customer-pct — Auto-preenche customerRetentionPct quando seleciona
  // cliente que tem `defaultRetentionPct` cadastrado. Só sobrescreve se o
  // campo estiver vazio (não atropela edição manual do operador).
  useEffect(() => {
    const pct = selectedCustomer?.defaultRetentionPct;
    if (pct == null) return;
    if (header.customerRetentionPct.trim() !== "") return;
    setHeader((prev) => ({ ...prev, customerRetentionPct: String(pct) }));
  }, [selectedCustomer?.defaultRetentionPct, header.customerRetentionPct]);

  function validateStep1(): string | null {
    if (header.billingCompanyId == null) return tr("dossierWizard.errSelectBillingCompany");
    if (header.customerId == null) return tr("dossierWizard.errSelectCustomer");
    if (!header.vehicleId && !header.plate.trim() && !header.vin.trim()) {
      return tr("dossierWizard.errVehicleRequired");
    }
    if (header.customerRetentionPct.trim() !== "") {
      const pct = Number(header.customerRetentionPct.replace(",", "."));
      if (!Number.isFinite(pct) || pct < 0 || pct > 100) {
        return tr("dossierWizard.errRetentionRange");
      }
    }
    return null;
  }

  function validateStep2(): string | null {
    if (!line.enabled) return null;
    if (line.responsibleCompanyId == null) return tr("dossierWizard.errResponsibleCompanyRequired");
    if (line.technicianId == null) return tr("dossierWizard.errTechnicianRequired");
    const orcamento = parseDecimal(line.orcamento);
    const faturado = parseDecimal(line.faturado);
    if (orcamento != null && orcamento < 0) return tr("dossierWizard.errQuoteMin");
    if (faturado != null && faturado < 0) return tr("dossierWizard.errInvoicedMin");
    if (line.technicianAmountKnown) {
      const pct = parseDecimal(line.technicianCommissionPct);
      if (pct != null && (pct < 0 || pct > 100)) {
        return tr("dossierWizard.errCommissionPctRange");
      }
      const amount = parseDecimal(line.technicianCommissionAmount);
      if (amount != null && amount < 0) return tr("dossierWizard.errCommissionAmountMin");
    } else {
      if (
        line.technicianCommissionPct.trim() !== "" ||
        line.technicianCommissionBase !== "" ||
        line.technicianCommissionAmount.trim() !== ""
      ) {
        return tr("dossierWizard.errAmountUnknownFieldsEmpty");
      }
    }
    return null;
  }

  function next() {
    setError(null);
    if (step === 1) {
      const err = validateStep1();
      if (err) {
        setError(err);
        return;
      }
      setStep(2);
    } else if (step === 2) {
      const err = validateStep2();
      if (err) {
        setError(err);
        return;
      }
      setStep(3);
    }
  }
  function back() {
    setError(null);
    if (step === 2) setStep(1);
    else if (step === 3) setStep(2);
  }

  async function handleSubmit(confirmNewService = false) {
    setError(null);
    setDuplicado(null);
    const e1 = validateStep1();
    if (e1) {
      setError(e1);
      setStep(1);
      return;
    }
    if (line.enabled) {
      const e2 = validateStep2();
      if (e2) {
        setError(e2);
        setStep(2);
        return;
      }
    }

    const headerPayload: {
      companyId: number;
      billingCompanyId: number;
      customerId: number;
      receivingCompanyId?: number;
      vehicleId?: number;
      plate?: string;
      vin?: string;
      invoiceNumberExternal?: string;
      invoiceDate?: string;
      customerRetentionPct?: number;
      notes?: string;
      confirmNewService?: boolean;
    } = {
      companyId,
      billingCompanyId: header.billingCompanyId!,
      customerId: header.customerId!,
    };
    if (header.receivingCompanyId != null)
      headerPayload.receivingCompanyId = header.receivingCompanyId;
    if (header.vehicleId != null) headerPayload.vehicleId = header.vehicleId;

    // Backend exige `plate OR vin` (CHECK no banco). Quando o user seleciona
    // veículo do dropdown, copiamos plate/vin do veículo como snapshot.
    // Inputs manuais sobrescrevem (caso o user queira corrigir o plate registrado).
    const selectedVehicle =
      header.vehicleId != null ? vehicles.find((v) => v.id === header.vehicleId) : null;
    const finalPlate = header.plate.trim() || selectedVehicle?.plate || "";
    const finalVin = header.vin.trim() || selectedVehicle?.vin || "";
    if (finalPlate) headerPayload.plate = finalPlate;
    if (finalVin) headerPayload.vin = finalVin;
    if (header.invoiceNumberExternal.trim())
      headerPayload.invoiceNumberExternal = header.invoiceNumberExternal.trim();
    if (header.invoiceDate) headerPayload.invoiceDate = header.invoiceDate;
    if (header.customerRetentionPct.trim() !== "")
      headerPayload.customerRetentionPct = Number(header.customerRetentionPct.replace(",", "."));
    if (header.notes.trim()) headerPayload.notes = header.notes.trim();
    if (confirmNewService) headerPayload.confirmNewService = true;

    let dossierId: number;
    try {
      const created = await createDossier.mutateAsync(headerPayload);
      dossierId = created.id;
    } catch (err) {
      const code = (err as { data?: { code?: string } } | null)?.data?.code;
      if (code === "CONFLICT") {
        setDuplicado(err instanceof Error ? err.message : String(err));
        return;
      }
      setError(err instanceof Error ? err.message : String(err));
      return;
    }

    if (line.enabled) {
      const linePayload: {
        companyId: number;
        dossierId: number;
        serviceType: ServiceType;
        responsibleCompanyId: number;
        technicianId: number;
        orcamento?: number | null;
        faturado?: number | null;
        technicianAmountKnown: boolean;
        technicianCommissionPct?: number | null;
        technicianCommissionBase?: Base | null;
        technicianCommissionAmount?: number | null;
        notes?: string;
      } = {
        companyId,
        dossierId,
        serviceType: line.serviceType,
        responsibleCompanyId: line.responsibleCompanyId!,
        technicianId: line.technicianId!,
        technicianAmountKnown: line.technicianAmountKnown,
      };
      const orc = parseDecimal(line.orcamento);
      const fat = parseDecimal(line.faturado);
      if (orc != null) linePayload.orcamento = orc;
      if (fat != null) linePayload.faturado = fat;
      if (line.technicianAmountKnown) {
        const pct = parseDecimal(line.technicianCommissionPct);
        const amount = parseDecimal(line.technicianCommissionAmount);
        if (pct != null) linePayload.technicianCommissionPct = pct;
        if (line.technicianCommissionBase !== "")
          linePayload.technicianCommissionBase = line.technicianCommissionBase;
        if (amount != null) linePayload.technicianCommissionAmount = amount;
      }
      if (line.notes.trim()) linePayload.notes = line.notes.trim();

      try {
        await createLine.mutateAsync(linePayload);
      } catch (err) {
        setError(
          `${tr("dossierWizard.errLineCreatePre")}: ${
            err instanceof Error ? err.message : String(err)
          }. ${tr("dossierWizard.errLineCreateSuf")}`,
        );
        // Mesmo com erro na linha, abre o detalhe
        setTimeout(() => {
          onCreated(dossierId);
          setLocation(`/dossiers/${dossierId}`);
        }, 2500);
        return;
      }
    }

    onCreated(dossierId);
    setLocation(`/dossiers/${dossierId}`);
  }

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50">
      <div className="w-full max-w-3xl bg-neutral-950 border border-neutral-800 rounded-lg shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="px-5 py-4 border-b border-neutral-800 flex items-center justify-between sticky top-0 bg-neutral-950 z-10">
          <div>
            <h2 className="text-lg font-semibold">{tr("dossierWizard.title")}</h2>
            <p className="text-xs text-neutral-500 mt-0.5">
              {tr("dossierWizard.stepLabelPre")} {step} {tr("dossierWizard.stepLabelSuf")} ·{" "}
              {step === 1
                ? tr("dossierWizard.stepHeader")
                : step === 2
                  ? tr("dossierWizard.stepFirstLine")
                  : tr("dossierWizard.stepReviewAndSave")}
            </p>
          </div>
          <button
            onClick={onClose}
            disabled={submitting}
            className="text-neutral-500 hover:text-neutral-300 text-lg leading-none disabled:opacity-40"
          >
            ×
          </button>
        </div>

        <Stepper step={step} />

        <div className="p-5 space-y-4">
          {step === 1 && (
            <Step1Header
              header={header}
              setHeader={setHeader}
              accessibleCompanies={accessibleCompanies}
              customers={customers}
              vehicles={vehicles}
              vehiclesLoading={header.customerId !== null && vehiclesQuery.isLoading}
              disabled={submitting}
            />
          )}
          {step === 2 && (
            <Step2Line
              line={line}
              setLine={setLine}
              accessibleCompanies={accessibleCompanies}
              technicians={technicians}
              disabled={submitting}
            />
          )}
          {step === 3 && (
            <Step3Review
              header={header}
              line={line}
              customerName={selectedCustomer?.displayName ?? "—"}
              technicianName={selectedTechnician?.name ?? "—"}
              billingCompanyName={
                accessibleCompanies.find((c) => c.id === header.billingCompanyId)?.name ?? "—"
              }
              receivingCompanyName={
                header.receivingCompanyId != null
                  ? (accessibleCompanies.find((c) => c.id === header.receivingCompanyId)?.name ??
                    "—")
                  : null
              }
              responsibleCompanyName={
                accessibleCompanies.find((c) => c.id === line.responsibleCompanyId)?.name ?? "—"
              }
            />
          )}

          {error && (
            <div className="text-red-400 text-sm bg-red-500/10 border border-red-500/30 rounded-md px-3 py-2 whitespace-pre-wrap">
              {error}
            </div>
          )}
          {duplicado && (
            <div
              className="space-y-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm text-amber-200"
              data-testid="dossier-duplicate-warning"
            >
              <p>⚠ {duplicado}</p>
              <button
                type="button"
                disabled={submitting}
                onClick={() => void handleSubmit(true)}
                className="rounded-md border border-amber-500/60 px-3 py-1 text-xs font-semibold text-amber-200 hover:bg-amber-500/15 disabled:opacity-40"
                data-testid="dossier-duplicate-confirm"
              >
                {tr("dossierWizard.dupConfirmBtn")}
              </button>
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-neutral-800 flex items-center justify-between sticky bottom-0 bg-neutral-950">
          <button
            onClick={onClose}
            disabled={submitting}
            className="rounded-md border border-neutral-800 hover:border-neutral-700 px-4 py-1.5 text-sm disabled:opacity-50"
          >
            {tr("dossierWizard.cancel")}
          </button>
          <div className="flex gap-2">
            {step > 1 && (
              <button
                onClick={back}
                disabled={submitting}
                className="rounded-md border border-neutral-800 hover:border-neutral-700 px-4 py-1.5 text-sm disabled:opacity-50"
              >
                {tr("dossierWizard.back")}
              </button>
            )}
            {step < 3 && (
              <button
                onClick={next}
                disabled={submitting}
                className="rounded-md bg-lime hover:bg-lime-bright text-on-lime disabled:opacity-50 px-4 py-1.5 text-sm font-medium"
              >
                {tr("dossierWizard.next")}
              </button>
            )}
            {step === 3 && (
              <button
                onClick={() => void handleSubmit()}
                disabled={submitting}
                className="rounded-md bg-lime hover:bg-lime-bright text-on-lime disabled:opacity-50 px-4 py-1.5 text-sm font-medium"
              >
                {submitting ? tr("dossierWizard.saving") : tr("dossierWizard.saveDossier")}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Stepper({ step }: { step: 1 | 2 | 3 }) {
  const t = useT();
  const items: { n: 1 | 2 | 3; label: string }[] = [
    { n: 1, label: t("dossierWizard.stepHeader") },
    { n: 2, label: t("dossierWizard.stepperLine") },
    { n: 3, label: t("dossierWizard.stepperReview") },
  ];
  return (
    <div className="px-5 py-3 border-b border-neutral-800 flex items-center gap-2 text-xs">
      {items.map((it, idx) => {
        const active = step === it.n;
        const done = step > it.n;
        return (
          <div key={it.n} className="flex items-center gap-2">
            <div
              className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${
                active
                  ? "bg-lime text-on-lime"
                  : done
                    ? "bg-lime/30 text-lime"
                    : "bg-neutral-800 text-neutral-500"
              }`}
            >
              {done ? "✓" : it.n}
            </div>
            <span className={active ? "text-lime" : done ? "text-neutral-400" : "text-neutral-600"}>
              {it.label}
            </span>
            {idx < items.length - 1 && <span className="text-neutral-700 mx-1">→</span>}
          </div>
        );
      })}
    </div>
  );
}

function Step1Header({
  header,
  setHeader,
  accessibleCompanies,
  customers,
  vehicles,
  vehiclesLoading,
  disabled,
}: {
  header: HeaderForm;
  setHeader: React.Dispatch<React.SetStateAction<HeaderForm>>;
  accessibleCompanies: ReadonlyArray<CompanyOption>;
  customers: ReadonlyArray<{ id: number; displayName: string }>;
  vehicles: ReadonlyArray<{
    id: number;
    plate: string | null;
    vin: string | null;
    make: string | null;
    model: string | null;
  }>;
  vehiclesLoading: boolean;
  disabled: boolean;
}) {
  const t = useT();
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <Field label={t("dossierWizard.fieldBillingCompany")} required>
          <select
            value={header.billingCompanyId ?? ""}
            onChange={(e) => setHeader({ ...header, billingCompanyId: Number(e.target.value) })}
            disabled={disabled}
            className={inputClass}
          >
            {accessibleCompanies.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label={t("dossierWizard.fieldReceivingCompany")}>
          <select
            value={header.receivingCompanyId ?? ""}
            onChange={(e) => {
              const v = e.target.value;
              setHeader({
                ...header,
                receivingCompanyId: v === "" ? null : Number(v),
              });
            }}
            disabled={disabled}
            className={inputClass}
          >
            <option value="">{t("dossierWizard.optionNone")}</option>
            {accessibleCompanies.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </Field>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <Field label={t("dossierWizard.fieldCustomer")} required>
          <select
            value={header.customerId ?? ""}
            onChange={(e) => {
              const v = e.target.value;
              setHeader({
                ...header,
                customerId: v === "" ? null : Number(v),
                vehicleId: null,
                plate: "",
                vin: "",
              });
            }}
            disabled={disabled}
            className={inputClass}
          >
            <option value="">{t("dossierWizard.optionSelect")}</option>
            {customers.map((c) => (
              <option key={c.id} value={c.id}>
                {c.displayName}
              </option>
            ))}
          </select>
        </Field>
        <Field label={t("dossierWizard.fieldExternalInvoiceNumber")}>
          <input
            value={header.invoiceNumberExternal}
            onChange={(e) => setHeader({ ...header, invoiceNumberExternal: e.target.value })}
            maxLength={64}
            disabled={disabled}
            placeholder={t("dossierWizard.placeholderExternalInvoiceNumber")}
            className={`${inputClass} font-mono`}
          />
        </Field>
      </div>

      <div className="rounded-md border border-neutral-800 bg-neutral-950/40 p-3 space-y-3">
        <p className="text-xs text-neutral-400 uppercase tracking-wider">
          {t("dossierWizard.sectionVehicle")}
        </p>
        <Field label={t("dossierWizard.fieldSelectVehicle")}>
          <select
            value={header.vehicleId ?? ""}
            onChange={(e) => {
              const v = e.target.value;
              setHeader({
                ...header,
                vehicleId: v === "" ? null : Number(v),
              });
            }}
            disabled={disabled || header.customerId == null || vehiclesLoading}
            className={inputClass}
          >
            <option value="">
              {header.customerId == null
                ? t("dossierWizard.optionSelectCustomerFirst")
                : vehiclesLoading
                  ? t("dossierWizard.loading")
                  : t("dossierWizard.optionNoVehicleLink")}
            </option>
            {vehicles.map((v) => (
              <option key={v.id} value={v.id}>
                {[v.plate, v.make, v.model].filter(Boolean).join(" · ") || v.vin || `#${v.id}`}
              </option>
            ))}
          </select>
        </Field>
        <p className="text-xs text-neutral-500">{t("dossierWizard.vehicleManualHint")}</p>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t("dossierWizard.fieldPlate")}>
            <input
              value={header.plate}
              onChange={(e) => setHeader({ ...header, plate: e.target.value })}
              maxLength={20}
              disabled={disabled || header.vehicleId !== null}
              placeholder={t("dossierWizard.placeholderPlate")}
              className={`${inputClass} font-mono`}
            />
          </Field>
          <Field label={t("dossierWizard.fieldVin")}>
            <input
              value={header.vin}
              onChange={(e) => setHeader({ ...header, vin: e.target.value })}
              maxLength={17}
              disabled={disabled || header.vehicleId !== null}
              placeholder={t("dossierWizard.placeholderOptional")}
              className={`${inputClass} font-mono`}
            />
          </Field>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <Field label={t("dossierWizard.fieldInvoiceDate")}>
          <input
            type="date"
            value={header.invoiceDate}
            onChange={(e) => setHeader({ ...header, invoiceDate: e.target.value })}
            disabled={disabled}
            className={inputClass}
          />
        </Field>
        <Field label={t("dossierWizard.fieldCustomerRetention")}>
          <input
            type="text"
            inputMode="decimal"
            value={header.customerRetentionPct}
            onChange={(e) => setHeader({ ...header, customerRetentionPct: e.target.value })}
            disabled={disabled}
            placeholder={t("dossierWizard.placeholderAutoFromContract")}
            className={`${inputClass} font-mono text-right`}
          />
        </Field>
      </div>

      <Field label={t("dossierWizard.fieldNotes")}>
        <textarea
          value={header.notes}
          onChange={(e) => setHeader({ ...header, notes: e.target.value })}
          maxLength={2000}
          rows={2}
          disabled={disabled}
          className={`${inputClass} resize-y w-full`}
        />
      </Field>
    </div>
  );
}

function Step2Line({
  line,
  setLine,
  accessibleCompanies,
  technicians,
  disabled,
}: {
  line: LineForm;
  setLine: React.Dispatch<React.SetStateAction<LineForm>>;
  accessibleCompanies: ReadonlyArray<CompanyOption>;
  technicians: ReadonlyArray<{
    id: number;
    name: string;
  }>;
  disabled: boolean;
}) {
  const tr = useT();
  return (
    <div className="space-y-4">
      <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
        <input
          type="checkbox"
          checked={line.enabled}
          onChange={(e) => setLine({ ...line, enabled: e.target.checked })}
          disabled={disabled}
          className="accent-lime"
        />
        <span>{tr("dossierWizard.addFirstLineNow")}</span>
        <span className="text-xs text-neutral-500">{tr("dossierWizard.addFirstLineHint")}</span>
      </label>

      {line.enabled && (
        <div className="space-y-4 border-t border-neutral-800 pt-4">
          <div className="grid grid-cols-2 gap-3">
            <Field label={tr("dossierWizard.fieldServiceType")} required>
              <select
                value={line.serviceType}
                onChange={(e) => setLine({ ...line, serviceType: e.target.value as ServiceType })}
                disabled={disabled}
                className={inputClass}
              >
                <option value="dsp">{tr("dossierWizard.optionServiceDsp")}</option>
                <option value="montage_demontage">
                  {tr("dossierWizard.optionServiceMontage")}
                </option>
              </select>
            </Field>
            <Field label={tr("dossierWizard.fieldResponsibleCompany")} required>
              <select
                value={line.responsibleCompanyId ?? ""}
                onChange={(e) =>
                  setLine({
                    ...line,
                    responsibleCompanyId: Number(e.target.value),
                  })
                }
                disabled={disabled}
                className={inputClass}
              >
                {accessibleCompanies.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </Field>
          </div>

          <Field label={tr("dossierWizard.fieldTechnician")} required>
            <select
              value={line.technicianId ?? ""}
              onChange={(e) => {
                const v = e.target.value;
                // F-tcc2 — sem prefill de comissão padrão: % e base vêm do par
                // técnico×cliente no backend (ou de override manual aqui).
                setLine({
                  ...line,
                  technicianId: v === "" ? null : Number(v),
                });
              }}
              disabled={disabled}
              className={inputClass}
            >
              <option value="">{tr("dossierWizard.optionSelect")}</option>
              {technicians.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label={tr("dossierWizard.fieldQuoteEur")}>
              <input
                type="text"
                inputMode="decimal"
                value={line.orcamento}
                onChange={(e) => setLine({ ...line, orcamento: e.target.value })}
                disabled={disabled}
                placeholder="0"
                className={`${inputClass} font-mono text-right`}
              />
            </Field>
            <Field label={tr("dossierWizard.fieldInvoicedEur")}>
              <input
                type="text"
                inputMode="decimal"
                value={line.faturado}
                onChange={(e) => setLine({ ...line, faturado: e.target.value })}
                disabled={disabled}
                placeholder="0"
                className={`${inputClass} font-mono text-right`}
              />
            </Field>
          </div>

          <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
            <input
              type="checkbox"
              checked={line.technicianAmountKnown}
              onChange={(e) =>
                setLine({
                  ...line,
                  technicianAmountKnown: e.target.checked,
                  technicianCommissionPct: e.target.checked ? line.technicianCommissionPct : "",
                  technicianCommissionBase: e.target.checked ? line.technicianCommissionBase : "",
                  technicianCommissionAmount: e.target.checked
                    ? line.technicianCommissionAmount
                    : "",
                })
              }
              disabled={disabled}
              className="accent-lime"
            />
            <span>{tr("dossierWizard.commissionAmountKnown")}</span>
          </label>

          {line.technicianAmountKnown && (
            <div className="grid grid-cols-3 gap-3">
              <Field label={tr("dossierWizard.fieldCommissionPct")}>
                <input
                  type="text"
                  inputMode="decimal"
                  value={line.technicianCommissionPct}
                  onChange={(e) => setLine({ ...line, technicianCommissionPct: e.target.value })}
                  disabled={disabled}
                  placeholder={tr("dossierWizard.placeholderAutoFromRecord")}
                  className={`${inputClass} font-mono text-right`}
                />
              </Field>
              <Field label={tr("dossierWizard.fieldBase")}>
                <select
                  value={line.technicianCommissionBase}
                  onChange={(e) =>
                    setLine({
                      ...line,
                      technicianCommissionBase: e.target.value as Base | "",
                    })
                  }
                  disabled={disabled}
                  className={inputClass}
                >
                  <option value="">{tr("dossierWizard.optionAuto")}</option>
                  <option value="orcamento">{tr("dossierWizard.optionBaseQuote")}</option>
                  <option value="faturado">{tr("dossierWizard.optionBaseInvoiced")}</option>
                </select>
              </Field>
              <Field label={tr("dossierWizard.fieldEffectiveAmountEur")}>
                <input
                  type="text"
                  inputMode="decimal"
                  value={line.technicianCommissionAmount}
                  onChange={(e) =>
                    setLine({
                      ...line,
                      technicianCommissionAmount: e.target.value,
                    })
                  }
                  disabled={disabled}
                  placeholder={tr("dossierWizard.placeholderAutoCalculated")}
                  className={`${inputClass} font-mono text-right`}
                />
              </Field>
            </div>
          )}

          <Field label={tr("dossierWizard.fieldNotes")}>
            <textarea
              value={line.notes}
              onChange={(e) => setLine({ ...line, notes: e.target.value })}
              maxLength={2000}
              rows={2}
              disabled={disabled}
              className={`${inputClass} resize-y w-full`}
            />
          </Field>
        </div>
      )}
    </div>
  );
}

function Step3Review({
  header,
  line,
  customerName,
  technicianName,
  billingCompanyName,
  receivingCompanyName,
  responsibleCompanyName,
}: {
  header: HeaderForm;
  line: LineForm;
  customerName: string;
  technicianName: string;
  billingCompanyName: string;
  receivingCompanyName: string | null;
  responsibleCompanyName: string;
}) {
  const t = useT();
  return (
    <div className="space-y-4">
      <section className="rounded-md border border-neutral-800 bg-neutral-950/40 p-4">
        <h3 className="text-sm font-semibold mb-3 text-neutral-300">
          {t("dossierWizard.stepHeader")}
        </h3>
        <dl className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
          <Row label={t("dossierWizard.rowBillingCompany")} value={billingCompanyName} />
          <Row label={t("dossierWizard.rowReceivingCompany")} value={receivingCompanyName ?? "—"} />
          <Row label={t("dossierWizard.fieldCustomer")} value={customerName} />
          <Row
            label={t("dossierWizard.rowExternalInvoiceNumber")}
            value={header.invoiceNumberExternal || t("dossierWizard.optionAuto")}
          />
          <Row
            label={t("dossierWizard.sectionVehicle")}
            value={
              header.vehicleId
                ? `#${header.vehicleId}`
                : [header.plate, header.vin].filter(Boolean).join(" / ") || "—"
            }
          />
          <Row label={t("dossierWizard.rowDate")} value={header.invoiceDate || "—"} />
          <Row
            label={t("dossierWizard.rowCustomerRetention")}
            value={header.customerRetentionPct || t("dossierWizard.placeholderAutoFromRecord")}
          />
        </dl>
        {header.notes && (
          <p className="text-xs text-neutral-500 mt-3 italic whitespace-pre-wrap">{header.notes}</p>
        )}
      </section>

      <section className="rounded-md border border-neutral-800 bg-neutral-950/40 p-4">
        <h3 className="text-sm font-semibold mb-3 text-neutral-300">
          {t("dossierWizard.stepFirstLine")}{" "}
          {!line.enabled && (
            <span className="text-neutral-500">{t("dossierWizard.lineNotCreated")}</span>
          )}
        </h3>
        {line.enabled ? (
          <dl className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
            <Row
              label={t("dossierWizard.rowType")}
              value={
                line.serviceType === "dsp"
                  ? t("dossierWizard.optionServiceDspShort")
                  : t("dossierWizard.optionServiceMontage")
              }
            />
            <Row
              label={t("dossierWizard.fieldResponsibleCompany")}
              value={responsibleCompanyName}
            />
            <Row label={t("dossierWizard.fieldTechnician")} value={technicianName} />
            <Row label={t("dossierWizard.rowQuote")} value={line.orcamento || "—"} />
            <Row label={t("dossierWizard.rowInvoiced")} value={line.faturado || "—"} />
            <Row
              label={t("dossierWizard.rowTechnicianCommission")}
              value={
                !line.technicianAmountKnown
                  ? t("dossierWizard.commissionUnknownValue")
                  : `${line.technicianCommissionPct || t("dossierWizard.optionAuto")}% ${line.technicianCommissionBase || t("dossierWizard.optionAuto")} → ${line.technicianCommissionAmount || t("dossierWizard.optionAuto")} €`
              }
            />
          </dl>
        ) : (
          <p className="text-sm text-neutral-500">{t("dossierWizard.noLineHint")}</p>
        )}
      </section>

      <div className="rounded-md bg-amber-500/5 border border-amber-500/30 px-3 py-2 text-xs text-amber-200">
        {t("dossierWizard.overrideNotice")}
      </div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <>
      <dt className="text-neutral-500 text-xs">{label}</dt>
      <dd className="text-neutral-200">{value}</dd>
    </>
  );
}

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="block text-xs text-neutral-400 mb-1">
        {label} {required && <span className="text-red-400">*</span>}
      </span>
      {children}
    </label>
  );
}

function parseDecimal(s: string): number | null {
  const t = s.trim();
  if (t === "") return null;
  const n = Number(t.replace(",", "."));
  return Number.isFinite(n) ? n : null;
}

const inputClass =
  "w-full rounded-md border border-neutral-800 bg-neutral-900 px-3 py-1.5 text-sm focus:border-lime outline-none disabled:opacity-50";
